import {computed, ref} from 'vue';
import {supabase} from '@/services/supabase';
import {LogCategory, logger} from '@/services/logger';
import { ForecastingProcess} from '@/types/supabase';
import {DOCUMENT_STATUS} from '@/types/document';
import {useAuth} from '@/composables/useAuth';
import {useTasks} from '@/composables/useTasks';

/**
 * Interface for document display data
 */
interface DocumentDisplay {
  id: string;
  report_name: string;
  quarter: string;
  due_date: string;
  status: DOCUMENT_STATUS;
  template_id: string;
  document_id: string;
}

/**
 * Composable for managing forecasting reports
 */
export function useReports() {
  // Constants
  const UPLOAD_TASK_DUE_DAYS = 2; // Number of days to complete upload task
  
  const documents = ref<DocumentDisplay[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);
  const availableQuarters = ref<{label: string, value: string}[]>([]);
  const selectedQuarter = ref<string>('');
  const documentsMap = ref<Map<string, any>>(new Map());
  
  // Get the current user's profile to access their report_type
  const { profile, isAuthenticated, checkAndRefreshAuth } = useAuth();
  
  // Add more detailed logging for profile
  const userReportType = computed(() => {
    logger.info('Computing userReportType', LogCategory.AUTH, {
      profileExists: !!profile.value,
      isAuthenticated: isAuthenticated.value,
      reportType: profile.value?.report_type,
      profileData: profile.value ? {
        id: profile.value.id,
        name: profile.value.name,
        business_role: profile.value.business_role,
        report_type: profile.value.report_type
      } : null
    });
    return profile.value?.report_type || null;
  });

  /**
   * Calculate the quarter from a date
   * @param dateString ISO date string
   * @returns Quarter string in format "Q1 2025"
   */
  function getQuarterFromDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Determine quarter based on month (0-11)
    let quarter;
    if (month < 3) {
      quarter = 1;
    } else if (month < 6) {
      quarter = 2;
    } else if (month < 9) {
      quarter = 3;
    } else {
      quarter = 4;
    }
    
    return `Q${quarter} ${year}`;
  }

  /**
   * Get the current quarter and year
   * @returns Current quarter string in format "Q1 2025"
   */
  function getCurrentQuarter(): string {
    return getQuarterFromDate(new Date().toISOString());
  }
  
  /**
   * Helper function that just returns the status (previously mapped between different enums)
   * @deprecated This function is kept for backward compatibility during transition
   */
  function mapDocumentStatus(status: DOCUMENT_STATUS): DOCUMENT_STATUS {
    // Now we just return the status directly
    return status;
  }
/**
   * Helper function to extract fileId from a URL string
   * @param url URL string that may contain a fileId
   * @returns The extracted fileId (filename after the last '/' character)
   */
  function extractFileIdFromUrl(url: string): string {
    if (!url) return '';
    
    // Extract the part after the last '/' character
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Fetch all documents from Supabase
   */
  async function fetchAllDocuments() {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Fetching documents from Supabase with status READY_FOR_DOWNLOAD, DOWNLOADED, UPLOADED_CHECKING, or ADJUSTMENT_NEEDED', LogCategory.DATABASE);
      
      // Always check and refresh authentication
      logger.info('Checking and refreshing authentication...', LogCategory.AUTH);
      const authResult = await checkAndRefreshAuth();
      
      logger.info('Authentication check result:', LogCategory.AUTH, {
        authResult,
        isAuthenticated: isAuthenticated.value,
        profileExists: !!profile.value
      });
      
      // If profile is still not loaded, wait a bit and try again
      if (!profile.value) {
        logger.info('Profile still not loaded, waiting...', LogCategory.AUTH);
        // Wait for a short time to allow profile to load
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check auth again
        await checkAndRefreshAuth();
      }
      
      // Force a re-computation of userReportType
      const reportType = userReportType.value;
      logger.info('Current report type is:', LogCategory.AUTH, {
        reportType,
        profileExists: !!profile.value,
        profileData: profile.value ? {
          id: profile.value.id,
          name: profile.value.name,
          business_role: profile.value.business_role,
          report_type: profile.value.report_type
        } : null
      });
      
      // Check if user is authenticated before proceeding
      if (!isAuthenticated.value) {
        logger.warn('User is not authenticated, cannot fetch documents', LogCategory.AUTH);
        documents.value = [];
        availableQuarters.value = [];
        return;
      }
      
      // Check if profile is loaded
      if (!profile.value) {
        logger.warn('User profile is not loaded, cannot fetch documents', LogCategory.AUTH);
        documents.value = [];
        availableQuarters.value = [];
        return;
      }
      
      logger.info('User profile is loaded', LogCategory.AUTH, {
        profileId: profile.value.id,
        reportType: profile.value.report_type
      });
      
      // Prepare the query
      let query = supabase
        .from('documents')
        .select('*')
        .in('status', [
          DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
          DOCUMENT_STATUS.DOWNLOADED,
          DOCUMENT_STATUS.UPLOADED_CHECKING,
          DOCUMENT_STATUS.ADJUSTMENT_NEEDED
        ]);
      
      // Filter by report_type if the user has one set
      if (userReportType.value) {
        logger.info('Filtering documents by user report type', LogCategory.DATABASE, {
          reportType: userReportType.value
        });
        query = query.eq('report_type', userReportType.value);
      } else {
        logger.info('No report type set for user, loading all documents with status READY_FOR_DOWNLOAD, DOWNLOADED, UPLOADED_CHECKING, or ADJUSTMENT_NEEDED', LogCategory.DATABASE);
      }
      
      // Execute the query
      const { data: docs, error: documentsError } = await query;
      
      if (documentsError) {
        logger.error('Error fetching documents', LogCategory.DATABASE, { error: documentsError });
        throw documentsError;
      }
      
      // Check if documents exist
      if (!docs || docs.length === 0) {
        // No documents found
        logger.info('No documents found with specified criteria', LogCategory.DATABASE);
        documents.value = [];
        availableQuarters.value = [];
        return;
      }
      
      logger.info(`Found ${docs.length} documents`, LogCategory.DATABASE);
      
      // Get all forecast_ids to fetch forecasting processes
      const forecastIds = docs.map(doc => doc.forecast_id).filter(id => id !== null);
      
      // Fetch forecasting processes for these documents
      const { data: forecastingProcesses, error: forecastError } = await supabase
        .from('forecasting_process')
        .select('*')
        .in('id', forecastIds);
      
      if (forecastError) {
        logger.error('Error fetching forecasting processes', LogCategory.DATABASE, { error: forecastError });
        throw forecastError;
      }
      
      // Create a map of forecasting processes by ID for easy lookup
      const forecastMap = new Map<string, ForecastingProcess>();
      forecastingProcesses?.forEach(process => {
        forecastMap.set(process.id, process);
      });
      
      // Group documents by quarter (using forecasting_process.name as quarter)
      const quarterGroups = new Map<string, any[]>();
      
      docs.forEach(doc => {
        if (doc.forecast_id && forecastMap.has(doc.forecast_id)) {
          const forecastProcess = forecastMap.get(doc.forecast_id);
          const quarter = forecastProcess?.name || getQuarterFromDate(doc.created_at);
          
          if (!quarterGroups.has(quarter)) {
            quarterGroups.set(quarter, []);
          }
          quarterGroups.get(quarter)?.push({
            document: doc,
            forecastProcess: forecastMap.get(doc.forecast_id)
          });
        } else {
          // Fallback if no forecast_id or process not found
          const quarter = getQuarterFromDate(doc.created_at);
          if (!quarterGroups.has(quarter)) {
            quarterGroups.set(quarter, []);
          }
          quarterGroups.get(quarter)?.push({
            document: doc,
            forecastProcess: null
          });
        }
      });
      
      // For each quarter, store all documents
      const quarterMap = new Map<string, any[]>();
      quarterGroups.forEach((items, quarter) => {
        // Sort by created_at in descending order (newest first)
        items.sort((a, b) =>
          new Date(b.document.created_at).getTime() - new Date(a.document.created_at).getTime()
        );
        quarterMap.set(quarter, items);
      });
      
      logger.info('Quarters with documents:', LogCategory.DATABASE, {
        quarters: Array.from(quarterMap.keys())
      });
      
      // Create dropdown options
      availableQuarters.value = Array.from(quarterMap.keys())
        .map(quarter => ({ label: quarter, value: quarter }))
        .sort((a, b) => {
          // Sort in anti-chronological order (newest first)
          const [aQ, aY] = a.value.split(' ');
          const [bQ, bY] = b.value.split(' ');
          
          if (aY !== bY) {
            return parseInt(bY) - parseInt(aY); // Sort by year (descending)
          }
          
          return parseInt(bQ.substring(1)) - parseInt(aQ.substring(1)); // Sort by quarter (descending)
        });
      
      // Set selected quarter to current quarter if available, otherwise first in list
      const currentQuarter = getCurrentQuarter();
      logger.info('Current quarter:', LogCategory.DATABASE, { currentQuarter });
      
      if (quarterMap.has(currentQuarter)) {
        selectedQuarter.value = currentQuarter;
        logger.info('Selected current quarter', LogCategory.DATABASE, { selectedQuarter: selectedQuarter.value });
      } else if (availableQuarters.value.length > 0) {
        selectedQuarter.value = availableQuarters.value[0].value;
        logger.info('Selected first available quarter', LogCategory.DATABASE, {
          selectedQuarter: selectedQuarter.value,
          availableQuarters: availableQuarters.value.map(q => q.value)
        });
      }
      
      // Store the quarter map for later use
      documentsMap.value = quarterMap;
      
      // Fetch report for selected quarter
      await fetchReportForQuarter(selectedQuarter.value);
    } catch (err) {
      logger.error('Error in fetchAllDocuments', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Fetch documents for a specific quarter
   */
  async function fetchReportForQuarter(quarter: string) {
    logger.info('Fetching documents for quarter', LogCategory.DATABASE, { quarter });
    
    if (!documentsMap.value.has(quarter)) {
      logger.info('No documents found for quarter', LogCategory.DATABASE, { quarter });
      documents.value = [];
      return;
    }
    
    const quarterDocuments = documentsMap.value.get(quarter);
    logger.info(`Found ${quarterDocuments.length} documents for quarter`, LogCategory.DATABASE, { quarter });
    
    try {
      // Process each document to get file information
      const processedDocuments: DocumentDisplay[] = [];
      
      for (const item of quarterDocuments) {
        const doc = item.document;
        const forecastProcess = item.forecastProcess;
        
        // Skip documents without file_id
        if (!doc.file_id) {
          logger.info('Document has no file_id, skipping', LogCategory.DATABASE, { document: doc });
          continue;
        }
        
        // Fetch file using file_id
        const { data: files, error: filesError } = await supabase
          .from('files')
          .select('id, name, type, url')
          .eq('id', doc.file_id);
        
        if (filesError) {
          logger.error('Error fetching file', LogCategory.DATABASE, { error: filesError });
          continue;
        }
        
        // Skip if no file found
        if (!files || files.length === 0) {
          logger.info('No file found with the specified file_id', LogCategory.DATABASE, { file_id: doc.file_id });
          continue;
        }
        
        const file = files[0];
        
        // Log document details for debugging
        logger.info('Processing document for display', LogCategory.DATABASE, {
          documentId: doc.id,
          documentUuid: doc.uuid,
          documentName: doc.name,
          fileId: file.id,
          fileName: file.name
        });
        
        // Create document display object
        processedDocuments.push({
          id: file.id,
          report_name: doc.name,
          quarter: forecastProcess?.name || quarter,
          due_date: forecastProcess?.end_date || new Date().toISOString(),
          status: mapDocumentStatus(doc.status),
          template_id: extractFileIdFromUrl(file.url), // Use file.url as template_id for download
          document_id: doc.id // Use doc.id instead of doc.uuid for comments reference
        });
      }
      
      documents.value = processedDocuments;
      logger.info(`Processed ${documents.value.length} documents for display`, LogCategory.DATABASE, {
        documents: documents.value.map(doc => ({
          id: doc.id,
          name: doc.report_name,
          quarter: doc.quarter,
          status: doc.status
        }))
      });
    } catch (err) {
      logger.error('Error in fetchReportForQuarter', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    }
  }
  
  /**
   * Change the selected quarter
   */
  async function changeQuarter(quarter: string) {
    selectedQuarter.value = quarter;
    await fetchReportForQuarter(quarter);
  }
  
  /**
   * Fetch reports from Supabase
   */
  /**
   * Fetch reports from Supabase
   */
  async function fetchReports() {
    await fetchAllDocuments();
  }

  /**
   * Download a template using the Fission Function
   * @param templateId The template ID (which is part of the url field from the files table)
   */
  /**
   * Update a document's status in the database
   * @param documentId The document ID
   * @param newStatus The new status to set
   * @returns True if the update was successful, false otherwise
   */
  async function updateDocumentStatus(documentId: string, newStatus: DOCUMENT_STATUS): Promise<boolean> {
    try {
      logger.info('Updating document status', LogCategory.DATABASE, { documentId, newStatus });
      
      // Get current user ID
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      
      if (!userId) {
        logger.warn('No user ID available for document status update', LogCategory.DATABASE, { documentId, newStatus });
      }
      
      // Update the document status in the database with updated_by and updated_at
      const { error: updateError } = await supabase
        .from('documents')
        .update({
          status: newStatus,
          updated_by: userId || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId);
      
      if (updateError) {
        logger.error('Error updating document status', LogCategory.DATABASE, { documentId, newStatus, error: updateError });
        return false;
      }
      
      logger.info('Document status updated successfully', LogCategory.DATABASE, {
        documentId,
        newStatus,
        updated_by: userId,
        updated_at: new Date().toISOString()
      });
      return true;
    } catch (err) {
      logger.error('Exception in updateDocumentStatus', LogCategory.DATABASE, { documentId, newStatus, error: err });
      return false;
    }
  }

  async function downloadTemplate(templateId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Downloading template via Fission Function', LogCategory.STORAGE, { templateId });
      
      // Find the document with the matching template_id
      const report = documents.value.find(doc => doc.template_id === templateId);
      
      if (!report) {
        logger.warn('No document found with the specified template_id', LogCategory.STORAGE, { templateId });
      }
      
      // Construct the URL for the Fission Function
      const fissionUrl = (process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + `template-download?templateId=${templateId}`;
      
      logger.info('Template download URL', LogCategory.STORAGE, { fissionUrl });
      
      // Create a download link and trigger the download
      const link = document.createElement('a');
      link.href = fissionUrl;
      link.target = '_blank'; // Open in a new tab
      link.rel = 'noopener noreferrer'; // Security best practice
      
      // Add to the document and click
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      logger.info('Template download initiated', LogCategory.STORAGE, { templateId });
      
      // Update the document status if we found a matching document
      if (report && report.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD) {
        try {
          // Update the status in the database
          const updateSuccess = await updateDocumentStatus(report.document_id, DOCUMENT_STATUS.DOWNLOADED);
          
          if (updateSuccess) {
            // Update the local state
            report.status = DOCUMENT_STATUS.DOWNLOADED;
            logger.info('Document status updated to DOWNLOADED', LogCategory.STORAGE, {
              documentId: report.document_id,
              templateId
            });
            
            // Get current user ID
            const { data: sessionData } = await supabase.auth.getSession();
            const userId = sessionData.session?.user.id;
            
            if (userId) {
              // Mark the associated download task as complete
              await completeDownloadTask(userId, report.document_id, report.report_name);
              
              // Create a new upload task
              await createUploadTask(userId, report.document_id, report.report_name);
            }
          } else {
            logger.warn('Failed to update document status in database', LogCategory.STORAGE, {
              documentId: report.document_id,
              templateId
            });
          }
        } catch (updateErr) {
          logger.error('Error updating document status', LogCategory.STORAGE, {
            documentId: report.document_id,
            templateId,
            error: updateErr
          });
        }
      }
      
      return true;
    } catch (err) {
      logger.error('Error in downloadTemplate', LogCategory.STORAGE, { templateId, error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      
      // Show error message to user
      alert(`Download failed: ${error.value.message || 'Unknown error'}`);
      
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Complete a download task for a specific document and user
   * @param userId The user ID
   * @param documentId The document ID
   * @param documentName The document name for the completion description
   * @returns Promise that resolves when the task is completed
   */
  async function completeDownloadTask(userId: string, documentId: string, documentName: string): Promise<void> {
    try {
      // Mark the associated download task as complete
      const { fetchTasks, completeTask } = useTasks();
      
      // Fetch all tasks
      const tasks = await fetchTasks();
      
      // Find the task with action=DOWNLOAD, assigned_user_id=userId, and document_id=documentId
      const downloadTask = tasks.find(task =>
        task.action === 'DOWNLOAD' &&
        task.assigned_user_id === userId &&
        task.document_id === documentId
      );
      
      if (downloadTask) {
        // Complete the task by setting done_at to current time
        const description = `Download completed for document ${documentName}`;
        const completedTask = await completeTask(downloadTask.id, description);
        
        if (completedTask) {
          logger.info('Download task marked as complete', LogCategory.DATABASE, {
            taskId: downloadTask.id,
            documentId,
            userId
          });
        } else {
          logger.warn('Failed to mark download task as complete', LogCategory.DATABASE, {
            taskId: downloadTask.id,
            documentId,
            userId
          });
        }
      } else {
        logger.info('No matching download task found for this document', LogCategory.DATABASE, {
          documentId,
          userId
        });
      }
    } catch (taskError) {
      logger.error('Error completing download task', LogCategory.DATABASE, {
        documentId,
        userId,
        error: taskError
      });
    }
  }

  /**
   * Create an upload task for a specific document and user
   * @param userId The user ID
   * @param documentId The document ID
   * @param documentName The document name for the task description
   * @returns Promise that resolves when the task is created
   */
  async function createUploadTask(userId: string, documentId: string, documentName: string): Promise<void> {
    try {
      // Get the tasks composable
      const { createTask } = useTasks();
      
      // Calculate due date (using constant for number of days)
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + UPLOAD_TASK_DUE_DAYS);
      
      // Format the due date for the description
      const formattedDueDate = dueDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Create the task
      const taskResult = await createTask({
        action: 'UPLOAD',
        assigned_user_id: userId,
        document_id: documentId,
        due_at: dueDate.toISOString(),
        description: `Please upload document ${documentName} until ${formattedDueDate}`
      });
      
      if (taskResult) {
        logger.info('Upload task created successfully', LogCategory.DATABASE, {
          taskId: taskResult.id,
          documentId,
          userId,
          dueDate: dueDate.toISOString()
        });
      } else {
        logger.warn('Failed to create upload task', LogCategory.DATABASE, {
          documentId,
          userId
        });
      }
    } catch (taskError) {
      logger.error('Error creating upload task', LogCategory.DATABASE, {
        documentId,
        userId,
        error: taskError
      });
    }
  }

  /**
   * Review a report
   * @param reportId The report ID
   */
  async function reviewReport(reportId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      logger.info('Reviewing report', LogCategory.DATABASE, { reportId });
      
      // In a real implementation, this would navigate to a review page
      // or open a modal with the report details
      
      // For now, we'll just log the action
      logger.info('Report review action triggered', LogCategory.DATABASE, { reportId });
      
      // Simulate a delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return true;
    } catch (err) {
      logger.error('Error in reviewReport', LogCategory.DATABASE, { reportId, error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    reports: documents, // Expose as 'reports' for backward compatibility
    loading,
    error,
    fetchReports,
    downloadTemplate,
    reviewReport,
    updateDocumentStatus,
    availableQuarters,
    selectedQuarter,
    changeQuarter
  };
}
